import api from '../api';
import type { User } from '../../types/user';

/**
 * Service function to get a list of all pending student accounts.
 * @returns A promise that resolves to an array of unapproved User objects.
 */
export const getPendingStudents = async (): Promise<User[]> => {
  try {
    const response = await api.get<User[]>('/approval/pending');
    return response.data;
  } catch (error: any) {
    if (error.response && error.response.data && error.response.data.message) {
      throw new Error(error.response.data.message);
    }
    throw new Error('Failed to fetch pending students.');
  }
};

/**
 * Service function to approve a student account.
 * @param studentId - The ID of the student to approve.
 * @returns A promise that resolves with a success message.
 */
export const approveStudent = async (studentId: string): Promise<string> => {
  try {
    const response = await api.patch<{ message: string }>(`/approval/approve/${studentId}`);
    return response.data.message;
  } catch (error: any) {
    if (error.response && error.response.data && error.response.data.message) {
      throw new Error(error.response.data.message);
    }
    throw new Error('Failed to approve student.');
  }
};

/**
 * Service function to reject (delete) a student account.
 * @param studentId - The ID of the student to reject and delete.
 * @returns A promise that resolves with a success message.
 */
export const rejectStudent = async (studentId: string): Promise<string> => {
  try {
    const response = await api.delete<{ message: string }>(`/approval/reject/${studentId}`);
    return response.data.message;
  } catch (error: any) {
    if (error.response && error.response.data && error.response.data.message) {
      throw new Error(error.response.data.message);
    }
    throw new Error('Failed to reject student.');
  }
};

// ✅ New: Service to get all students
/**
 * Service function to get a list of all students.
 * @returns A promise that resolves to an array of User objects.
 */
export const getAllStudents = async (): Promise<User[]> => {
  try {
    const response = await api.get<User[]>('/mentor/students');
    return response.data;
  } catch (error: any) {
    if (error.response && error.response.data && error.response.data.message) {
      throw new Error(error.response.data.message);
    }
    throw new Error('Failed to fetch students.');
  }
};